import type { BaseEnvelope } from '../janus-engine/compile/base-patch';
import { compileActionTiming } from '../shared/action-timing';
import { markStall } from '../shared/stall-mark';
import type { FromBaseEntryResult } from '../janus-engine/compile/from-base-entry';
import { compileRevisionKey, responseBase } from '../janus-engine/compile/worker-protocol';
import type { CompileRequest, CompileReply, CompileProgress, CacheBodyReply, CompileSnapshot } from '../janus-engine/compile/worker-protocol';
import type { CachePutBody, CachePutSkip } from './cache-put-body';
import { createTreeReceiver, postTree } from '../janus-engine/compile/tree-transfer';
import type { TreePart } from '../janus-engine/compile/tree-transfer';
import type { SavedReadResponse } from '../shared/read-saved-response';
import { longRestWorkerInput, longRestWorkerPatch } from '../janus-engine/compile/long-rest-worker';
import type { LongRestWorkerInput, LongRestStaticScan } from '../janus-engine/compile/long-rest-worker';
import type { PossessorCompileContext, PossessorCompileResult, PossessorStatusMutation } from '../janus-engine/compile/possessor-compile';
import type { SpawnWorkerInput } from '../janus-engine/spawn/preview';
import type { CasterArr } from '../shared/array-types';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers';
import { readCreatureStatuses } from '../janus-engine/compile/creature-status-reader';
import { encounterCreatureField } from '../shared/array-types';
import { normalizePhpListish } from '../shared/php-listish';

/** Transfer only the held state read by aura gates, never a full creature graph. */
export function heldAuraOwners(envelope: BaseEnvelope): Record<string, Record<string, unknown>> {
    if (typeof window === 'undefined') return {};
    const ids = new Set(Object.values(readCreatureStatuses(envelope.base)).map(row => readLayerListEntryUuid((row as Record<string, unknown>)?.status_caster_id).toUpperCase()));
    const found: Record<string, Record<string, unknown>> = {};
    const ec = envelope.base.encounter_creatures;
    const encounterId = String(encounterCreatureField(ec, 'encounter_id') ?? '');
    const encounter = window.FateTide?.encounterCache?.get(encounterId);
    const roster = normalizePhpListish(encounter?.encounter_creatures_arr) as CasterArr[];
    for (const id of ids) {
        const row = roster.find(row => String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === id)
            ?? window.FateTide?.casterCache?.get(id);
        if (!row) continue;
        found[id] = { encounter_creatures: Object.fromEntries(['encounter_creature_id', 'v_current', 'v_temp', 'encounter_creature_dead', 'encounter_creature_deleted']
            .map(key => [key, encounterCreatureField(row.encounter_creatures, key)])), computed: { vitality: { v_max: ftComputedValue(row, 'vitality', 'v_max') } },
            statuses: { target: Object.values(readCreatureStatuses(row)).map(status => {
                const entry = status as Record<string, unknown>;
                return { status_item_id: entry.status_item_id ?? ftItemsCol(entry.status_arr, 'item_id'), status_deleted: entry.status_deleted };
            }) } };
    }
    return found;
}
import { workerTreePatch, applyWorkerTreePatch } from '../janus-engine/compile/worker-tree-patch';

/** This instance belongs to the websocket bundle. Other bundles use its window bridge. */
export function createCompileWorker(engine: string, url: string, timeoutMs = 120_000, ceilingMs = 900_000,
    engineMismatch?: (next?: string) => void) {
    let worker: Worker | undefined, sequence = 0, failed: string | undefined;
    let members = { compiles: 0, hits: 0 };
    let memberCompiles: Record<string, number> = {};
    const flights = new Map<string, { envelope: BaseEnvelope; promise: Promise<FromBaseEntryResult> }>();
    type Input = Pick<BaseEnvelope, 'base' | 'metadata'>;
    type Completed = { sequence: number; envelope: BaseEnvelope; result: FromBaseEntryResult };
    const completed = new Map<string, Completed>();
    const waiting = new Map<number, { envelope: BaseEnvelope; input?: Input; previous?: Completed; sendBase(): void; resolve(result: FromBaseEntryResult): void; reject(error: Error): void }>();
    const snapshots = new WeakMap<object, CompileSnapshot>();
    const catalogs = new Map<number, { resolve(value: unknown): void; reject(error: Error): void }>();
    const receiver = createTreeReceiver();
    const revisions = new Map<string, number>();
    const rawRefs = new WeakMap<object, { token: number; key: string; metadata: unknown }>();
    const transport = { fullTransfers: 0, rawRefs: 0, referenceMisses: 0, inputPatches: 0, resultPatches: 0, inputWrites: 0, resultWrites: 0 };
    const restInputs = new WeakMap<object, { sequence: number; input: LongRestWorkerInput; scan: LongRestStaticScan }>();
    const watchdogs = new Map<number, ReturnType<typeof setTimeout>>();
    let idle: ReturnType<typeof setTimeout> | undefined, progress = 0;
    // All requests share one worker/serial queue. An active member or transport
    // part proves that queued requests are still being serviced too.
    const touch = () => {
        clearTimeout(idle);
        idle = watchdogs.size ? setTimeout(() => fail('compile-worker-timeout'), timeoutMs) : undefined;
    };
    const clearWatchdog = (token: number) => {
        clearTimeout(watchdogs.get(token)); watchdogs.delete(token);
        if (!watchdogs.size) { clearTimeout(idle); idle = undefined; }
    };
    const watch = (token: number) => {
        watchdogs.set(token, setTimeout(() => fail('compile-worker-timeout'), ceilingMs));
        // Posting more work is not evidence that the worker is alive.
        if (idle === undefined) touch();
    };
    const fail = (reason: string, next?: string) => {
        failed = reason; worker?.terminate();
        for (const token of watchdogs.keys()) clearWatchdog(token);
        for (const token of waiting.keys()) receiver.discard('compile-result', token);
        for (const token of catalogs.keys()) for (const tag of ['read', 'catalog', 'long-rest', 'spawn-preview']) receiver.discard(tag, token);
        for (const pending of waiting.values()) pending.reject(new Error(reason));
        for (const pending of catalogs.values()) pending.reject(new Error(reason));
        catalogs.clear();
        waiting.clear(); flights.clear();
        // Waiters are settled and watchdogs cleared first: a page hook that throws
        // (banner, telemetry) must never leave a compile pending or a ceiling timer live.
        if (reason === 'compile-worker-engine-mismatch') engineMismatch?.(next);
    };
    function start(): Worker {
        if (failed) throw new Error(failed);
        if (worker) return worker;
        try { worker = new Worker(url, { type: 'module', name: 'janus-from-base' }); }
        catch { fail('compile-worker-unavailable'); throw new Error('compile-worker-unavailable'); }
        worker.onerror = () => fail('compile-worker-unavailable');
        worker.onmessage = ({ data }: MessageEvent<CompileReply | CompileProgress | TreePart | CacheBodyReply | { kind: 'catalog-error' | 'read-error'; sequence: number; error: string; engine?: string }>) => {
            if ('kind' in data && data.kind === 'compile-progress') {
                if (watchdogs.has(data.sequence) && Number.isSafeInteger(data.progress) && data.progress > progress) {
                    progress = data.progress; touch();
                }
                return;
            }
            if (watchdogs.has(data.sequence)) touch();
            if ('kind' in data && data.kind === 'cache-body') {
                clearWatchdog(data.sequence);
                if (data.engine !== engine) { fail('compile-worker-engine-mismatch', data.engine); return; }
                catalogs.get(data.sequence)?.resolve(data); catalogs.delete(data.sequence); return;
            }
            if ('kind' in data && (data.kind === 'catalog-error' || data.kind === 'read-error')) {
                clearWatchdog(data.sequence);
                if (data.error === 'compile-worker-engine-mismatch') { fail(data.error, data.engine); return; }
                catalogs.get(data.sequence)?.reject(new Error(data.error)); catalogs.delete(data.sequence); return;
            }
            if ('kind' in data && data.kind === 'tree-part') {
                if (data.tag === 'compile-result') {
                    if (!waiting.has(data.sequence)) return;
                    const complete = receiver.receive(data);
                    if (!complete) return;
                    data = complete.value as CompileReply;
                } else {
                if (!catalogs.has(data.sequence)) return;
                const complete = receiver.receive(data);
                if (complete) { clearWatchdog(data.sequence); catalogs.get(data.sequence)?.resolve(complete.value); catalogs.delete(data.sequence); }
                return;
                }
            }
            data = data as CompileReply;
            const pending = waiting.get(data.sequence);
            if (!pending) return;
            if (data.engine !== engine || data.error === 'compile-worker-engine-mismatch') { waiting.delete(data.sequence); fail('compile-worker-engine-mismatch', data.engine); pending.reject(new Error('compile-worker-engine-mismatch')); return; }
            if (data.needsBase) { transport.referenceMisses++; pending.sendBase(); return; }
            waiting.delete(data.sequence);
            clearWatchdog(data.sequence);
            const expected = pending.envelope;
            if (data.entity !== expected.entity || data.entity_id !== expected.entity_id || data.base_rev !== expected.base_rev) {
                pending.reject(new Error('compile-worker-revision-mismatch')); return;
            }
            members = data.members;
            if (data.memberCompiles) memberCompiles = data.memberCompiles;
            if (data.result_patch && pending.previous?.sequence === data.result_patch.previous) {
                data.result = applyWorkerTreePatch(pending.previous.result, data.result_patch.changes) as FromBaseEntryResult;
                transport.resultPatches++;
                transport.resultWrites += data.result_patch.changes.set.length + data.result_patch.changes.unset.length;
            }
            if (data.error || !data.result) { pending.reject(new Error(data.error ?? 'compile-worker-output')); return; }
            const identity = `${expected.entity}:${expected.entity_id.toUpperCase()}`;
            completed.delete(identity); completed.set(identity, { sequence: data.sequence, envelope: { ...expected, ...pending.input }, result: data.result });
            while (completed.size > 8) completed.delete(completed.keys().next().value!);
            if (data.snapshot && data.result.computed && typeof data.result.computed === 'object') snapshots.set(data.result.computed, data.snapshot);
            pending.resolve(data.result);
        };
        return worker;
    }
    function compile(envelope: BaseEnvelope): Promise<FromBaseEntryResult> {
        markStall('compile:' + envelope.entity);
        const key = compileRevisionKey(envelope), previous = flights.get(key);
        if (previous) return previous.envelope.base === envelope.base && previous.envelope.metadata === envelope.metadata ? previous.promise
            : previous.promise.catch(() => {}).then(() => compile(envelope));
        const finishTiming = compileActionTiming(envelope.entity);
        const job = new Promise<FromBaseEntryResult>((resolve, reject) => {
            const channel = start(), token = ++sequence;
            watch(token);
            const identity = `${envelope.entity}:${envelope.entity_id.toUpperCase()}`;
            revisions.set(identity, envelope.base_rev);
            channel.postMessage({ kind: 'revision', entity: envelope.entity, entity_id: envelope.entity_id, base_rev: envelope.base_rev, sequence: token });
            const header = { entity: envelope.entity, entity_id: envelope.entity_id, base_rev: envelope.base_rev,
                engine: envelope.engine, catalog_version: envelope.catalog_version };
            const auraOwners = heldAuraOwners(envelope);
            // Keep the tree already assembled for transport, never the caller's mutable saved rows. Local pickup/resource
            // projections can change those rows without a compile; the next patch must still describe the worker's input.
            const inputReceiver = createTreeReceiver();
            const sendInput = (part: TreePart) => {
                const received = inputReceiver.receive(part);
                if (received) {
                    const request = received.value as CompileRequest & { envelope: BaseEnvelope };
                    const pending = waiting.get(token);
                    if (pending) pending.input = request.input_patch
                        ? applyWorkerTreePatch({ base: prior!.envelope.base, metadata: prior!.envelope.metadata }, request.input_patch.changes) as Input
                        : { base: request.envelope.base, metadata: request.envelope.metadata };
                }
                channel.postMessage(part);
            };
            let sentBase = false;
            const sendBase = () => {
                if (sentBase) { clearWatchdog(token); waiting.delete(token); reject(new Error('compile-worker-input-protocol')); return; }
                sentBase = true; transport.fullTransfers++;
                // Browser-only viewer/cache state never enters the compiler.
                void postTree(sendInput, 'compile', token, { sequence: token, engine,
                    envelope: { ...header, base: envelope.base, metadata: envelope.metadata }, auraOwners, result_ref: prior?.sequence },
                    () => waiting.has(token) && revisions.get(identity) === envelope.base_rev).then(sent => {
                        if (!sent) {
                            if (waiting.has(token)) channel.postMessage({ kind: 'discard', sequence: token });
                            clearWatchdog(token); waiting.delete(token); reject(new Error('compile-worker-superseded'));
                        }
                    }).catch(error => { clearWatchdog(token); waiting.delete(token); reject(error); });
            };
            const prior = completed.get(identity);
            const patchable = prior && prior.envelope.base !== envelope.base && prior.envelope.catalog_version === envelope.catalog_version;
            waiting.set(token, { envelope: { ...envelope }, previous: prior, sendBase, resolve, reject });
            const reference = rawRefs.get(envelope.base);
            if (reference?.key === key && reference.metadata === envelope.metadata) {
                transport.rawRefs++;
                channel.postMessage({ sequence: token, engine, raw_ref: reference.token, result_ref: prior?.sequence, envelope: { ...header, base: {} }, auraOwners });
            } else if (patchable) {
                const changes = workerTreePatch({ base: prior.envelope.base, metadata: prior.envelope.metadata },
                    { base: envelope.base, metadata: envelope.metadata });
                transport.inputPatches++; transport.inputWrites += changes.set.length + changes.unset.length;
                void postTree(sendInput, 'compile', token, { sequence: token, engine,
                    envelope: { ...header, base: {} }, input_patch: { previous: prior.sequence, changes }, auraOwners },
                    () => waiting.has(token)).catch(error => { clearWatchdog(token); waiting.delete(token); reject(error); });
            } else sendBase();
        }).finally(() => { finishTiming?.(); if (flights.get(key)?.promise === job) flights.delete(key); });
        flights.set(key, { envelope, promise: job }); return job;
    }
    function catalog(json: string | ArrayBuffer): Promise<unknown> {
        return new Promise((resolve, reject) => {
            const token = ++sequence, channel = start();
            watch(token);
            catalogs.set(token, { resolve, reject }); channel.postMessage({ kind: 'catalog', sequence: token, json, engine },
                typeof json === 'string' ? [] : [json]);
        });
    }
    async function read(url: string, init: RequestInit = {}): Promise<SavedReadResponse> {
        const signal = init.signal;
        if (signal?.aborted) throw signal.reason;
        const value = await new Promise<unknown>((resolve, reject) => {
            const token = ++sequence, channel = start();
            watch(token);
            const abort = () => {
                clearWatchdog(token);
                catalogs.delete(token); receiver.discard('read', token);
                channel.postMessage({ kind: 'abort-read', sequence: token }); reject(signal?.reason);
            };
            const clean = () => signal?.removeEventListener('abort', abort);
            catalogs.set(token, { resolve(value) { clean(); resolve(value); }, reject(error) { clean(); reject(error); } });
            signal?.addEventListener('abort', abort, { once: true });
            channel.postMessage({ kind: 'read', sequence: token, engine, url,
                init: { ...init, signal: undefined, headers: [...new Headers(init.headers)] } });
        }) as { ok: boolean; status: number; statusText: string; headers: [string, string][]; value: unknown; raw_ref?: number };
        const base = responseBase(value.value);
        if (base && value.raw_ref !== undefined) rawRefs.set(base.base, { token: value.raw_ref, key: compileRevisionKey(base), metadata: base.metadata });
        return { ok: value.ok, status: value.status, statusText: value.statusText, headers: new Headers(value.headers), json: async () => value.value };
    }
    const link = (original: BaseEnvelope, full: BaseEnvelope) => {
        const reference = rawRefs.get(original.base);
        if (reference) rawRefs.set(full.base, { ...reference, metadata: full.metadata });
    };
    const retainSnapshot = (source: unknown, copy: unknown): void => {
        if (source && copy && typeof source === 'object' && typeof copy === 'object') {
            const snapshot = snapshots.get(source); if (snapshot) snapshots.set(copy, snapshot);
        }
    };
    const discard = (envelope: BaseEnvelope): void => {
        for (const [token, pending] of waiting) if (compileRevisionKey(pending.envelope) === compileRevisionKey(envelope)) {
            worker?.postMessage({ kind: 'discard', sequence: token });
            receiver.discard('compile-result', token);
            clearWatchdog(token); waiting.delete(token); pending.reject(new Error('compile-worker-discarded'));
        }
    };
    async function longRest(row: Record<string, unknown>, chain: PossessorStatusMutation[], context: PossessorCompileContext, carrier?: object): Promise<PossessorCompileResult> {
        const previous = carrier && restInputs.get(carrier);
        // Fetching missing charges replaces the reader context. Its immutable
        // carrier/catalog inputs still match; refreshed charges travel in the patch.
        const sameContext = previous && (Object.keys(previous.input.context) as (keyof LongRestWorkerInput['context'])[])
            .every(key => previous.input.context[key] === context[key]);
        const reuse = previous && previous.input.row === row && previous.input.chain === chain && sameContext ? previous : undefined;
        const scan: LongRestStaticScan = reuse?.scan ?? { objects: new WeakSet() };
        const input = await longRestWorkerInput(row, chain, context, scan);
        return send(reuse);
        async function send(held?: NonNullable<typeof reuse>): Promise<PossessorCompileResult> {
            const token = ++sequence, channel = start(); watch(token);
            const result = await new Promise<PossessorCompileResult>((resolve, reject) => {
                catalogs.set(token, { resolve: value => resolve(value as PossessorCompileResult), reject });
                const request = held ? { engine, previous: held.sequence, patch: longRestWorkerPatch(held.input, input) } : { engine, input };
                void postTree(part => channel.postMessage(part), 'long-rest', token, request, undefined, 512)
                    .catch(error => { clearWatchdog(token); catalogs.delete(token); reject(error); });
            });
            if (held && !result.ok && result.reason === 'long-rest-input-reference-missing') return send();
            if (carrier) restInputs.set(carrier, { sequence: token, input, scan });
            return result;
        }
    }
    async function previewSpawn(input: SpawnWorkerInput): Promise<Record<string, unknown>> {
        markStall('definition-adoption:' + ('itemId' in input ? input.itemId : 'spawn-births'));
        const token = ++sequence, channel = start(); watch(token);
        return new Promise((resolve, reject) => {
            catalogs.set(token, { resolve: value => resolve(value as Record<string, unknown>), reject });
            void postTree(part => channel.postMessage(part), 'spawn-preview', token, { engine, input })
                .catch(error => { clearWatchdog(token); catalogs.delete(token); reject(error); });
        });
    }
    async function cacheBody(json: string | CompileSnapshot, onSkip?: (skip: CachePutSkip) => void): Promise<CachePutBody | null> {
        const reply = await new Promise<unknown>((resolve, reject) => {
            const token = ++sequence, channel = start(); watch(token);
            catalogs.set(token, { resolve, reject }); channel.postMessage({ kind: 'cache-body', sequence: token, engine, json });
        }) as CacheBodyReply;
        if (reply.skip) onSkip?.(reply.skip);
        return reply.request;
    }
    /** Boot the worker ahead of its first request (a relocation lane warms while the movers' bases are read); a start failure reports on that request. */
    function warm(): void {
        if (failed || worker) return;
        try { start(); } catch { /* the first compile reports compile-worker-unavailable */ }
    }
    return { compile, catalog, read, link, longRest, previewSpawn, cacheBody, discard, retainSnapshot, warm, transportStats: () => ({ ...transport }),
        snapshot: (computed: object) => snapshots.get(computed), stats: () => ({ ...members }), memberCompiles: () => ({ ...memberCompiles }) };
}
